import {
  FeatureName,
  cacheKeys,
  useIsFeatureEnabled,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AssetHeader,
  ChevronRightIcon,
  LoadingIndicator,
  SlideAction,
  type SlideLayoutLocationState,
  getAutoPromptProps,
  tokens,
  useResponsiveDesign,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useAssetRouting } from '../assetRouteContext';
import {
  RoutineSubmitSource,
  isActivityOrEditPage,
  shouldUseAssetSubrouteInitialTransition,
} from '../assetUtils';
import { AssetActivityFold } from './assetActivityFold';
import { AssetCard as AssetCardOld } from './assetCard-old';
import { AssetMediaFold } from './assetMediaFold';
import { AssetRoutineFold } from './assetRoutineFold';

export const AssetCard = ({
  assetId,
  forceShowing,
}: {
  assetId: string;
  forceShowing?: boolean;
}) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <AssetCardBase assetId={assetId} forceShowing={forceShowing} />;
  }

  return <AssetCardOld assetId={assetId} />;
};

export const AssetCardBase = ({
  assetId,
  forceShowing,
}: {
  assetId: string;
  forceShowing?: boolean;
}) => {
  const { onSlideChange, prepareSlideTransition, setColumnTitle } =
    useSlideLayout();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition = locationState?.slideLayoutInitialTransition;
  const isSlideTransitionMount =
    initialTransition === SlideAction.NEXT ||
    initialTransition === SlideAction.PREV;
  // Read the context issue ID from the URL (present when inside an activities route).
  const { issueId: contextIssueId } = useParams();
  const { basePath, isWorkspaceMode } = useAssetRouting(assetId);
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery: invalidateWorkspaceAsset,
  } = useWorkspaceAsset(assetId, !assetId);
  const navigate = useNavigate();
  const isResponsiveDesign = useResponsiveDesign();
  const queryClient = useQueryClient();
  const isEditPage = isActivityOrEditPage(location);
  const isWeb = Platform.OS === 'web';

  // Reset component state when assetId changes
  useEffect(() => {
    if (isSlideTransitionMount) return;

    invalidateWorkspaceAsset();
  }, [assetId, invalidateWorkspaceAsset, isSlideTransitionMount]);

  // Auto-prompt routines on asset open for mobile only
  useEffect(() => {
    if (isWeb || isLoadingWorkspaceAsset || !workspaceAsset) {
      return;
    }

    if (workspaceAsset.asset?.id !== assetId) {
      return;
    }

    if (!isEditPage) {
      const autoPrompt = getAutoPromptProps(workspaceAsset);

      if (autoPrompt) {
        navigate(
          `/assets/${assetId}/routines/${autoPrompt.first.routine?.id}`,
          { state: { from: RoutineSubmitSource.AssetCard } }
        );
      }
    }
  }, [isLoadingWorkspaceAsset, workspaceAsset, assetId, isEditPage]);

  useEffect(() => {
    if (workspaceAsset) {
      setColumnTitle(workspaceAsset.asset?.name || 'assets.menu.title');
    }
  }, [workspaceAsset, setColumnTitle]);

  useEffect(() => {
    return () => {
      setColumnTitle(undefined);
    };
  }, [setColumnTitle]);

  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);

  const onIssueSelected = async (issueId: string) => {
    setSelectedIssueId(issueId);
    const nextPath = `${basePath}/activity/${issueId}`;

    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(nextPath, {
        state: {
          from: RoutineSubmitSource.AssetCard,
          slideLayoutInitialTransition: SlideAction.NEXT,
        },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    // In activities context use the route's contextIssueId (the activity the
    // user was viewing when they opened the asset panel) as the first segment.
    // Using the selected issueId for both positions would lose the original context.
    navigate(nextPath, {
      state: { from: RoutineSubmitSource.AssetCard },
    });
  };

  const onAssetEdit = async () => {
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/edit`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    startTransition(() => navigate(`${basePath}/edit`));
  };

  const onHeroImagePress = async () => {
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/image`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    startTransition(() => navigate(`${basePath}/image`));
  };

  const viewAssetInfoLink = !location.pathname.includes('edit') ? (
    <Pressable style={styles.viewAssetButton} onPress={onAssetEdit}>
      <Text style={styles.viewAssetButtonLabel}>
        {`${t(
          isWorkspaceMode ? 'topNav.moreWorkspaceInfo' : 'topNav.moreAssetInfo'
        )}`}
      </Text>
      <ChevronRightIcon size={20} color={tokens.colors.neutral[600]} />
    </Pressable>
  ) : null;

  if (!assetId) {
    return null;
  }

  if (isLoadingWorkspaceAsset || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  return (
    workspaceAsset !== undefined && (
      <View style={styles.container}>
        <View style={styles.containerHeader}>
          <AssetHeader
            workspaceAsset={workspaceAsset!}
            additionalHeaderBlock={viewAssetInfoLink}
            assetDetailsContainerStyle={styles.assetDetailsContainerStyle}
            imgSize={isResponsiveDesign ? 85 : 112}
            invalidateQuery={async () => await invalidateWorkspaceAsset()}
            mediaPicker={MediaPicker}
            onImagePress={onHeroImagePress}
          />
        </View>
        <AssetRoutineFold assetId={assetId} forceShowing={forceShowing} />
        <AssetActivityFold
          assetId={assetId}
          onIssueSelected={onIssueSelected}
          selectedIssue={selectedIssueId}
          showAssetName={false}
          forceShowing={forceShowing}
        />
        <AssetMediaFold
          workspaceAsset={workspaceAsset}
          assetId={assetId}
          forceShowing={forceShowing}
        />
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerHeader: {
    paddingTop: 6,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: tokens.colors.brand.white,
  },
  assetDetailsContainerStyle: {
    height: 112,
    justifyContent: 'space-between',
  },
  viewAssetButton: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 24,
    marginTop: 5,
    paddingVertical: 0,
    marginVertical: 0,
  },
  viewAssetButtonLabel: {
    ...tokens.typography.label.medium,
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
    color: tokens.colors.neutral[600],
    marginRight: 0,
    padding: 0,
  },
});
